/**
 * NER-1276 — Single source of truth for contextual help + feature tours.
 *
 * Every help topic lives here. The registry derives, with no extra wiring:
 *   - inline `<HelpIcon topicKey="..." />` content (title + description)
 *   - route-aware sub-tours (TourContext matches `location.pathname` to `route`)
 *   - the Help Center menu in the Header (topics grouped by `feature`)
 *   - per-feature "completed" flags in localStorage (keyed by `feature`)
 *
 * HOW TO ADD A TOPIC: see src/help/README.md. In short — add an entry whose
 * key is `{feature}.{concept}`, point `titleKey`/`descriptionKey` at i18n keys
 * under `help.*` in es/en/pt, and (optionally) add a `tour` array. The
 * registry test validates that every i18n key resolves in all three locales.
 *
 * NOTE: this module is intentionally free of any import that transitively pulls
 * in `services/api.js` (axios is ESM and breaks CRA/jest). Keep it pure so
 * `registry.test.js` and `scripts/check-help-coverage.js` can read it cheaply.
 */

export const HELP_REGISTRY = {
  // ---- Dashboard --------------------------------------------------------
  'dashboard.overview': {
    feature: 'dashboard',
    // The dashboard is the landing route, mounted at "/" (App.js), not
    // "/dashboard" (which doesn't exist). Login navigates to "/", so the
    // auto-tour must match "/" or it never fires. NER-1276 QA fix.
    route: '/',
    titleKey: 'help.dashboard.overview.title',
    descriptionKey: 'help.dashboard.overview.description',
    tour: [
      {
        target: '.activity-bar',
        titleKey: 'help.dashboard.overview.steps.0.title',
        contentKey: 'help.dashboard.overview.steps.0.content',
        placement: 'right',
      },
      {
        target: '.header-new',
        titleKey: 'help.dashboard.overview.steps.1.title',
        contentKey: 'help.dashboard.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.dock-panel',
        titleKey: 'help.dashboard.overview.steps.2.title',
        contentKey: 'help.dashboard.overview.steps.2.content',
        placement: 'left',
      },
      {
        target: '.dashboard-stats',
        titleKey: 'help.dashboard.overview.steps.3.title',
        contentKey: 'help.dashboard.overview.steps.3.content',
        placement: 'bottom',
      },
      {
        target: '.quick-actions',
        titleKey: 'help.dashboard.overview.steps.4.title',
        contentKey: 'help.dashboard.overview.steps.4.content',
        placement: 'top',
      },
    ],
  },
  'dashboard.stats': {
    feature: 'dashboard',
    titleKey: 'help.dashboard.stats.title',
    descriptionKey: 'help.dashboard.stats.description',
  },
  'dashboard.quickActions': {
    feature: 'dashboard',
    titleKey: 'help.dashboard.quickActions.title',
    descriptionKey: 'help.dashboard.quickActions.description',
  },

  // ---- Menu -------------------------------------------------------------
  'menu.overview': {
    feature: 'menu',
    route: '/menu',
    titleKey: 'help.menu.overview.title',
    descriptionKey: 'help.menu.overview.description',
    tour: [
      {
        target: '.menu-page-header',
        titleKey: 'help.menu.overview.steps.0.title',
        contentKey: 'help.menu.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.category-list',
        titleKey: 'help.menu.overview.steps.1.title',
        contentKey: 'help.menu.overview.steps.1.content',
        placement: 'right',
      },
      {
        target: '.add-item-btn',
        titleKey: 'help.menu.overview.steps.2.title',
        contentKey: 'help.menu.overview.steps.2.content',
        placement: 'left',
      },
      {
        target: '.product-card',
        titleKey: 'help.menu.overview.steps.3.title',
        contentKey: 'help.menu.overview.steps.3.content',
        placement: 'bottom',
      },
      {
        target: '.product-availability',
        titleKey: 'help.menu.overview.steps.4.title',
        contentKey: 'help.menu.overview.steps.4.content',
        placement: 'left',
      },
    ],
  },

  // ---- Orders -----------------------------------------------------------
  'orders.overview': {
    feature: 'orders',
    route: '/orders',
    titleKey: 'help.orders.overview.title',
    descriptionKey: 'help.orders.overview.description',
    tour: [
      {
        target: '.orders-header',
        titleKey: 'help.orders.overview.steps.0.title',
        contentKey: 'help.orders.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.order-filters',
        titleKey: 'help.orders.overview.steps.1.title',
        contentKey: 'help.orders.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.order-card',
        titleKey: 'help.orders.overview.steps.2.title',
        contentKey: 'help.orders.overview.steps.2.content',
        placement: 'right',
      },
      {
        target: '.order-status-btn',
        titleKey: 'help.orders.overview.steps.3.title',
        contentKey: 'help.orders.overview.steps.3.content',
        placement: 'left',
      },
      {
        target: '.order-print-btn',
        titleKey: 'help.orders.overview.steps.4.title',
        contentKey: 'help.orders.overview.steps.4.content',
        placement: 'left',
      },
    ],
  },

  // ---- Modifiers (NER-1279) ---------------------------------------------
  'modifiers.overview': {
    feature: 'modifiers',
    route: '/menu/modifiers',
    titleKey: 'help.modifiers.overview.title',
    descriptionKey: 'help.modifiers.overview.description',
    tour: [
      {
        target: '.modifiers-page',
        titleKey: 'help.modifiers.overview.steps.0.title',
        contentKey: 'help.modifiers.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.page-header .btn-primary',
        titleKey: 'help.modifiers.overview.steps.1.title',
        contentKey: 'help.modifiers.overview.steps.1.content',
        placement: 'left',
      },
      {
        target: '.grid-container',
        titleKey: 'help.modifiers.overview.steps.2.title',
        contentKey: 'help.modifiers.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.grid-container',
        titleKey: 'help.modifiers.overview.steps.3.title',
        contentKey: 'help.modifiers.overview.steps.3.content',
        placement: 'top',
      },
    ],
  },
  'modifiers.minMaxSelect': {
    feature: 'modifiers',
    titleKey: 'help.modifiers.minMaxSelect.title',
    descriptionKey: 'help.modifiers.minMaxSelect.description',
  },
  'modifiers.required': {
    feature: 'modifiers',
    titleKey: 'help.modifiers.required.title',
    descriptionKey: 'help.modifiers.required.description',
  },

  // ---- Floor plan (NER-1279) — tab-embedded, manual tour (no route) -----
  'floorplan.overview': {
    feature: 'floorplan',
    titleKey: 'help.floorplan.overview.title',
    descriptionKey: 'help.floorplan.overview.description',
    tour: [
      {
        target: '.fp-editor',
        titleKey: 'help.floorplan.overview.steps.0.title',
        contentKey: 'help.floorplan.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.fp-sidebar',
        titleKey: 'help.floorplan.overview.steps.1.title',
        contentKey: 'help.floorplan.overview.steps.1.content',
        placement: 'left',
      },
      {
        target: '.fp-canvas',
        titleKey: 'help.floorplan.overview.steps.2.title',
        contentKey: 'help.floorplan.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.fp-toolbar-actions',
        titleKey: 'help.floorplan.overview.steps.3.title',
        contentKey: 'help.floorplan.overview.steps.3.content',
        placement: 'bottom',
      },
    ],
  },
  'floorplan.grouping': {
    feature: 'floorplan',
    titleKey: 'help.floorplan.grouping.title',
    descriptionKey: 'help.floorplan.grouping.description',
  },
  'floorplan.shapes': {
    feature: 'floorplan',
    titleKey: 'help.floorplan.shapes.title',
    descriptionKey: 'help.floorplan.shapes.description',
  },

  // ---- Invoicing (NER-1279) ---------------------------------------------
  'invoicing.overview': {
    feature: 'invoicing',
    route: '/invoicing',
    titleKey: 'help.invoicing.overview.title',
    descriptionKey: 'help.invoicing.overview.description',
    tour: [
      {
        target: '.invoicing-page',
        titleKey: 'help.invoicing.overview.steps.0.title',
        contentKey: 'help.invoicing.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.invoicing-totals',
        titleKey: 'help.invoicing.overview.steps.1.title',
        contentKey: 'help.invoicing.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.invoicing-filters',
        titleKey: 'help.invoicing.overview.steps.2.title',
        contentKey: 'help.invoicing.overview.steps.2.content',
        placement: 'bottom',
      },
      {
        target: '.invoicing-table-wrapper',
        titleKey: 'help.invoicing.overview.steps.3.title',
        contentKey: 'help.invoicing.overview.steps.3.content',
        placement: 'top',
      },
    ],
  },
  'invoicing.types': {
    feature: 'invoicing',
    titleKey: 'help.invoicing.types.title',
    descriptionKey: 'help.invoicing.types.description',
  },
  'invoicing.creditDebitNotes': {
    feature: 'invoicing',
    titleKey: 'help.invoicing.creditDebitNotes.title',
    descriptionKey: 'help.invoicing.creditDebitNotes.description',
  },
  'invoicing.cae': {
    feature: 'invoicing',
    titleKey: 'help.invoicing.cae.title',
    descriptionKey: 'help.invoicing.cae.description',
  },

  // ---- Fiscal config (NER-1279) -----------------------------------------
  'fiscal.overview': {
    feature: 'fiscal',
    route: '/fiscal-config',
    titleKey: 'help.fiscal.overview.title',
    descriptionKey: 'help.fiscal.overview.description',
    tour: [
      {
        target: '.fiscal-config-page',
        titleKey: 'help.fiscal.overview.steps.0.title',
        contentKey: 'help.fiscal.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.fiscal-status-bar',
        titleKey: 'help.fiscal.overview.steps.1.title',
        contentKey: 'help.fiscal.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '#fiscal-condicion-iva',
        titleKey: 'help.fiscal.overview.steps.2.title',
        contentKey: 'help.fiscal.overview.steps.2.content',
        placement: 'right',
      },
      {
        target: '.fiscal-cert-mode-toggle',
        titleKey: 'help.fiscal.overview.steps.3.title',
        contentKey: 'help.fiscal.overview.steps.3.content',
        placement: 'top',
      },
    ],
  },
  'fiscal.condicionIVA': {
    feature: 'fiscal',
    titleKey: 'help.fiscal.condicionIVA.title',
    descriptionKey: 'help.fiscal.condicionIVA.description',
  },
  'fiscal.puntoVenta': {
    feature: 'fiscal',
    titleKey: 'help.fiscal.puntoVenta.title',
    descriptionKey: 'help.fiscal.puntoVenta.description',
  },
  'fiscal.certificate': {
    feature: 'fiscal',
    titleKey: 'help.fiscal.certificate.title',
    descriptionKey: 'help.fiscal.certificate.description',
  },
  'fiscal.environment': {
    feature: 'fiscal',
    titleKey: 'help.fiscal.environment.title',
    descriptionKey: 'help.fiscal.environment.description',
  },

  // ---- Custom domains (NER-1279) — tab-embedded, manual tour (no route) -
  'customDomains.overview': {
    feature: 'customDomains',
    titleKey: 'help.customDomains.overview.title',
    descriptionKey: 'help.customDomains.overview.description',
    tour: [
      {
        target: '.custom-domains-form',
        titleKey: 'help.customDomains.overview.steps.0.title',
        contentKey: 'help.customDomains.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.custom-domains-instructions',
        titleKey: 'help.customDomains.overview.steps.1.title',
        contentKey: 'help.customDomains.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.custom-domains-add',
        titleKey: 'help.customDomains.overview.steps.2.title',
        contentKey: 'help.customDomains.overview.steps.2.content',
        placement: 'bottom',
      },
      {
        target: '.custom-domains-form',
        titleKey: 'help.customDomains.overview.steps.3.title',
        contentKey: 'help.customDomains.overview.steps.3.content',
        placement: 'top',
      },
    ],
  },
  'customDomains.cnameTypes': {
    feature: 'customDomains',
    titleKey: 'help.customDomains.cnameTypes.title',
    descriptionKey: 'help.customDomains.cnameTypes.description',
  },
  'customDomains.propagation': {
    feature: 'customDomains',
    titleKey: 'help.customDomains.propagation.title',
    descriptionKey: 'help.customDomains.propagation.description',
  },

  // ---- Purchase orders / Compras (NER-1279) -----------------------------
  'purchaseOrders.overview': {
    feature: 'purchaseOrders',
    route: '/inventory/purchase-orders',
    titleKey: 'help.purchaseOrders.overview.title',
    descriptionKey: 'help.purchaseOrders.overview.description',
    tour: [
      {
        target: '.purchase-orders-page',
        titleKey: 'help.purchaseOrders.overview.steps.0.title',
        contentKey: 'help.purchaseOrders.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.filters-bar',
        titleKey: 'help.purchaseOrders.overview.steps.1.title',
        contentKey: 'help.purchaseOrders.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.purchase-orders-table',
        titleKey: 'help.purchaseOrders.overview.steps.2.title',
        contentKey: 'help.purchaseOrders.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.purchase-orders-table',
        titleKey: 'help.purchaseOrders.overview.steps.3.title',
        contentKey: 'help.purchaseOrders.overview.steps.3.content',
        placement: 'top',
      },
    ],
  },
  'purchaseOrders.statusFlow': {
    feature: 'purchaseOrders',
    titleKey: 'help.purchaseOrders.statusFlow.title',
    descriptionKey: 'help.purchaseOrders.statusFlow.description',
  },
  'purchaseOrders.stockImpact': {
    feature: 'purchaseOrders',
    titleKey: 'help.purchaseOrders.stockImpact.title',
    descriptionKey: 'help.purchaseOrders.stockImpact.description',
  },
  'purchaseOrders.vsExpenses': {
    feature: 'purchaseOrders',
    titleKey: 'help.purchaseOrders.vsExpenses.title',
    descriptionKey: 'help.purchaseOrders.vsExpenses.description',
  },

  // ---- Cash register / Caja (NER-1279) ----------------------------------
  'cashRegister.overview': {
    feature: 'cashRegister',
    route: '/cash-register',
    titleKey: 'help.cashRegister.overview.title',
    descriptionKey: 'help.cashRegister.overview.description',
    tour: [
      {
        target: '.cash-register-page',
        titleKey: 'help.cashRegister.overview.steps.0.title',
        contentKey: 'help.cashRegister.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.register-selector',
        titleKey: 'help.cashRegister.overview.steps.1.title',
        contentKey: 'help.cashRegister.overview.steps.1.content',
        placement: 'right',
      },
      {
        target: '.register-content',
        titleKey: 'help.cashRegister.overview.steps.2.title',
        contentKey: 'help.cashRegister.overview.steps.2.content',
        placement: 'left',
      },
      {
        target: '.register-content',
        titleKey: 'help.cashRegister.overview.steps.3.title',
        contentKey: 'help.cashRegister.overview.steps.3.content',
        placement: 'left',
      },
    ],
  },
  'cashRegister.shift': {
    feature: 'cashRegister',
    titleKey: 'help.cashRegister.shift.title',
    descriptionKey: 'help.cashRegister.shift.description',
  },
  'cashRegister.expectedVsDeclared': {
    feature: 'cashRegister',
    titleKey: 'help.cashRegister.expectedVsDeclared.title',
    descriptionKey: 'help.cashRegister.expectedVsDeclared.description',
  },
  'cashRegister.whatCounts': {
    feature: 'cashRegister',
    titleKey: 'help.cashRegister.whatCounts.title',
    descriptionKey: 'help.cashRegister.whatCounts.description',
  },

  // ---- Expenses / Gastos (NER-1279) -------------------------------------
  'expenses.overview': {
    feature: 'expenses',
    route: '/expenses',
    titleKey: 'help.expenses.overview.title',
    descriptionKey: 'help.expenses.overview.description',
    tour: [
      {
        target: '.expenses-page',
        titleKey: 'help.expenses.overview.steps.0.title',
        contentKey: 'help.expenses.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.tabs',
        titleKey: 'help.expenses.overview.steps.1.title',
        contentKey: 'help.expenses.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.expenses-table',
        titleKey: 'help.expenses.overview.steps.2.title',
        contentKey: 'help.expenses.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.expenses-table',
        titleKey: 'help.expenses.overview.steps.3.title',
        contentKey: 'help.expenses.overview.steps.3.content',
        placement: 'top',
      },
    ],
  },
  'expenses.vsPurchases': {
    feature: 'expenses',
    titleKey: 'help.expenses.vsPurchases.title',
    descriptionKey: 'help.expenses.vsPurchases.description',
  },
  'expenses.category': {
    feature: 'expenses',
    titleKey: 'help.expenses.category.title',
    descriptionKey: 'help.expenses.category.description',
  },

  // ---- Subscription / Plan (NER-1279) — OWNER ---------------------------
  'subscription.overview': {
    feature: 'subscription',
    route: '/subscription',
    titleKey: 'help.subscription.overview.title',
    descriptionKey: 'help.subscription.overview.description',
    tour: [
      {
        target: '.subscription-page',
        titleKey: 'help.subscription.overview.steps.0.title',
        contentKey: 'help.subscription.overview.steps.0.content',
        placement: 'bottom',
      },
      {
        target: '.plan-card',
        titleKey: 'help.subscription.overview.steps.1.title',
        contentKey: 'help.subscription.overview.steps.1.content',
        placement: 'bottom',
      },
      {
        target: '.usage-section',
        titleKey: 'help.subscription.overview.steps.2.title',
        contentKey: 'help.subscription.overview.steps.2.content',
        placement: 'top',
      },
      {
        target: '.plan-card',
        titleKey: 'help.subscription.overview.steps.3.title',
        contentKey: 'help.subscription.overview.steps.3.content',
        placement: 'bottom',
      },
    ],
  },
  'subscription.tiers': {
    feature: 'subscription',
    titleKey: 'help.subscription.tiers.title',
    descriptionKey: 'help.subscription.tiers.description',
  },
  'subscription.limits': {
    feature: 'subscription',
    titleKey: 'help.subscription.limits.title',
    descriptionKey: 'help.subscription.limits.description',
  },
  'subscription.overage': {
    feature: 'subscription',
    titleKey: 'help.subscription.overage.title',
    descriptionKey: 'help.subscription.overage.description',
  },
  'subscription.billingCycle': {
    feature: 'subscription',
    titleKey: 'help.subscription.billingCycle.title',
    descriptionKey: 'help.subscription.billingCycle.description',
  },

  // ---- Payment methods (NER-1285 inherit + NER-1378 scope) — HelpIcon-only -
  // Single feature ("Métodos de pago") covering BOTH scopes so the Help Center
  // shows ONE group, not a duplicate — that duplicate-looking layout is exactly
  // the scope confusion NER-1376 reported. `scope` is the canonical "where does
  // each setting live" explainer; `inherit`/`orgDefaults` are per-concept icons.
  'paymentMethods.scope': {
    feature: 'paymentMethods',
    titleKey: 'help.paymentMethods.scope.title',
    descriptionKey: 'help.paymentMethods.scope.description',
  },
  'paymentMethods.inherit': {
    feature: 'paymentMethods',
    titleKey: 'help.paymentMethods.inherit.title',
    descriptionKey: 'help.paymentMethods.inherit.description',
  },
  'paymentMethods.orgDefaults': {
    feature: 'paymentMethods',
    titleKey: 'help.paymentMethods.orgDefaults.title',
    descriptionKey: 'help.paymentMethods.orgDefaults.description',
  },

  // ---- Auto-close sessions (NER-1279) — single field, HelpIcon-only -----
  'autoClose.minutes': {
    feature: 'autoClose',
    titleKey: 'help.autoClose.minutes.title',
    descriptionKey: 'help.autoClose.minutes.description',
  },
  'autoClose.recommendation': {
    feature: 'autoClose',
    titleKey: 'help.autoClose.recommendation.title',
    descriptionKey: 'help.autoClose.recommendation.description',
  },
};

/** Order in which features appear in the Help Center menu. Unknown features
 *  are appended alphabetically after these. */
const FEATURE_ORDER = [
  'dashboard',
  'menu',
  'orders',
  'tables',
  'inventory',
  'modifiers',
  'floorplan',
  'invoicing',
  'fiscal',
  'customDomains',
  'purchaseOrders',
  'cashRegister',
  'expenses',
  'subscription',
  'paymentMethods',
  'autoClose',
];

/** Returns the topic entry for a key, or undefined. */
export function getTopic(topicKey) {
  return HELP_REGISTRY[topicKey];
}

/** All topic keys (used by tests + the coverage lint script). */
export function getAllTopicKeys() {
  return Object.keys(HELP_REGISTRY);
}

/**
 * Pure route → feature resolver for auto-triggered sub-tours.
 * Returns the `feature` of the registry entry whose `route` best matches
 * `pathname` (longest matching route wins), only considering entries that
 * actually define a `tour`. Returns null when nothing matches.
 */
export function getAutoTourFeature(pathname) {
  if (!pathname) return null;
  let best = null;
  let bestLen = -1;
  for (const entry of Object.values(HELP_REGISTRY)) {
    if (!entry.route || !Array.isArray(entry.tour) || entry.tour.length === 0) continue;
    const matches = pathname === entry.route || pathname.startsWith(`${entry.route}/`);
    if (matches && entry.route.length > bestLen) {
      best = entry.feature;
      bestLen = entry.route.length;
    }
  }
  return best;
}

/**
 * Returns the tour steps for a feature (first registry entry of that feature
 * that defines a non-empty `tour`), or an empty array.
 */
export function getTourForFeature(feature) {
  for (const entry of Object.values(HELP_REGISTRY)) {
    if (entry.feature === feature && Array.isArray(entry.tour) && entry.tour.length > 0) {
      return entry.tour;
    }
  }
  return [];
}

/** True if the feature has at least one defined tour. */
export function featureHasTour(feature) {
  return getTourForFeature(feature).length > 0;
}

/**
 * Topics grouped by feature for the Help Center menu, in FEATURE_ORDER.
 * Returns: [{ feature, topics: [{ topicKey, ...entry }] }]
 */
export function getHelpCenterGroups() {
  const byFeature = {};
  for (const [topicKey, entry] of Object.entries(HELP_REGISTRY)) {
    if (!byFeature[entry.feature]) byFeature[entry.feature] = [];
    byFeature[entry.feature].push({ topicKey, ...entry });
  }
  const features = Object.keys(byFeature).sort((a, b) => {
    const ia = FEATURE_ORDER.indexOf(a);
    const ib = FEATURE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return features.map((feature) => ({ feature, topics: byFeature[feature] }));
}
